import { ChevronRightIcon, InfinityIcon } from "lucide-react";
import type { PackForSaleRow } from "@/actions/packs";
import Link from "@/components/ui/link";
import { formatPriceCompact } from "@/lib/currency";
import { cn } from "@/lib/utils";
import { perBookingMinor, savingAgainst } from "../lib/pricing";
import { hueForHiive } from "./pack-pass-shell";
import { savingSpread } from "./pack-sale-card";
import type { PackStripContext } from "./pack-strip";

/**
 * The packs a hiive sells, as Wallet-style cards — laid open, never stacked.
 *
 * An earlier version overlapped these the way Apple Wallet collapses a stack,
 * showing each card's top band and only the front card in full. It looked
 * right and was wrong: a pack is chosen on its *terms* — how many bookings,
 * what each works out at, how long it lasts — and every one of those sat in the
 * hidden part of the card. A buyer comparing three packs could read three names
 * and three prices and nothing that distinguishes them.
 *
 * So: the card material stays (the hiive's hue, the tier ramp, the catch of
 * light along the top edge) and the overlap goes. Nothing is behind anything.
 */
export function PackWalletCards({
  packs,
  hiiveSlug,
  heading = "Save with a pack",
  context,
  className,
}: {
  packs: PackForSaleRow[];
  hiiveSlug: string;
  heading?: string;
  /** A price in front of the buyer, when the surface knows one. */
  context?: PackStripContext;
  className?: string;
}) {
  if (packs.length === 0) return null;

  const bestPercent = (pack: PackForSaleRow): number => {
    const perBooking = perBookingMinor(pack);
    const named = savingAgainst(perBooking, context?.listPriceMinor);
    if (named) return named.percent;
    return savingSpread(pack, perBooking)?.high ?? 0;
  };

  // Best first: with nothing overlapping, reading order is top-down and the
  // strongest argument should be the one read first.
  const ordered = [...packs].sort((a, b) => bestPercent(b) - bestPercent(a));

  return (
    <section className={className}>
      <h2 className="mb-3 text-xl font-semibold text-text-primary">
        {heading}
      </h2>

      <div className="flex flex-col gap-3">
        {ordered.map((pack, index) => (
          <WalletCard
            key={pack.packId}
            pack={pack}
            hiiveSlug={hiiveSlug}
            context={context}
            // Richest card first, fading back down the list — the ramp reads as
            // tiers rather than as three identical rectangles, because hue is
            // the *hiive's* identity and every pack it sells shares one.
            tintStep={Math.max(0, ordered.length - 1 - index)}
          />
        ))}
      </div>
    </section>
  );
}

function WalletCard({
  pack,
  hiiveSlug,
  context,
  tintStep,
}: {
  pack: PackForSaleRow;
  hiiveSlug: string;
  context?: PackStripContext;
  tintStep: number;
}) {
  const perBooking = perBookingMinor(pack);
  const named = savingAgainst(perBooking, context?.listPriceMinor);
  const spread = named ? null : savingSpread(pack, perBooking);
  const percentLabel = named
    ? `Save ${named.percent}%`
    : spread
      ? spread.low === spread.high
        ? `Save ${spread.low}%`
        : `Save ${spread.low}–${spread.high}%`
      : null;

  // The CYCLE, for a batch pack: four classes, not five bookings. The extra
  // credit buys something else entirely — an optional monthly session that is
  // often not even scheduled yet — and counting it here made the pack read as
  // five interchangeable classes at a price none of them cost.
  const bookings =
    pack.kind === "credit" && pack.creditsPerBooking
      ? pack.classesPerCycle != null
        ? pack.classesPerCycle
        : pack.creditCount
          ? Math.floor(pack.creditCount / Math.max(1, pack.creditsPerBooking))
          : null
      : null;

  const validity =
    pack.validityDays == null
      ? "No expiry"
      : pack.clockStart === "first_use"
        ? `Valid ${pack.validityDays} days from your first booking`
        : `Valid ${pack.validityDays} days`;

  return (
    <Link
      href={`/hiives/${hiiveSlug}/packs/${pack.slug}`}
      style={
        {
          "--pass-hue": hueForHiive(hiiveSlug),
          "--pass-tint": `${tintStep * 3}%`,
        } as React.CSSProperties
      }
      className={cn(
        "pack-pass-face relative block overflow-hidden rounded-[1.4rem]",
        "shadow-[0_2px_10px_-4px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.14)]",
        "ring-1 ring-black/[0.06] ring-inset dark:ring-white/[0.06]",
        "transition-transform duration-200 ease-[var(--ease-out-strong)] active:scale-[0.985]",
      )}
    >
      <div className="flex items-center gap-2.5 px-4 pt-4">
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-text-primary">
          {pack.name}
        </span>
        {percentLabel ? (
          <span className="shrink-0 rounded-full bg-black/[0.08] px-2 py-0.5 text-[10px] font-semibold tracking-wide text-success uppercase dark:bg-black/25 dark:text-[#F5E6B8]">
            {percentLabel}
          </span>
        ) : null}
        <span className="shrink-0 text-[15px] font-semibold text-text-primary tabular-nums">
          {formatPriceCompact(pack.priceAmountMinor, pack.currency)}
        </span>
        <ChevronRightIcon
          className="size-4 shrink-0 text-text-secondary"
          aria-hidden="true"
        />
      </div>

      <div className="px-4 pt-3 pb-4">
        <div className="flex items-end gap-2.5">
          <span className="text-[2.1rem] leading-none font-semibold text-text-primary tabular-nums">
            {pack.kind === "window" ? (
              <InfinityIcon className="size-8" aria-hidden="true" />
            ) : (
              (bookings ?? pack.creditCount)
            )}
          </span>
          <span className="pb-0.5 text-[13px] text-text-secondary">
            {pack.kind === "window"
              ? "Unlimited access"
              : pack.classesPerCycle != null
                ? pack.classesPerCycle === 1
                  ? "class in your batch"
                  : "classes in your batch"
                : bookings != null
                  ? bookings === 1
                    ? "booking"
                    : "bookings"
                  : "credits"}
            {perBooking != null
              ? ` · ${formatPriceCompact(perBooking, pack.currency)} each`
              : ""}
          </span>
        </div>
        {/* The bonus, named and set apart. It is not a fifth class and should
            not be counted as one — it is occasional, optional, and the reason
            the pack can be sold before the session even exists. */}
        {pack.bonusLabel ? (
          <p className="mt-1.5 text-[12px] text-text-secondary">
            + {pack.bonusLabel}
          </p>
        ) : null}

        {/* The before-and-after, wherever a real price was handed in. On every
            card now — this is the line that decides between two packs, and it
            was the first casualty of the overlap. */}
        {named && context ? (
          <p className="mt-2 text-[12.5px] leading-snug text-success dark:text-[#F5E6B8]">
            <span className="text-text-secondary/70 line-through">
              {formatPriceCompact(named.listPriceMinor, pack.currency)}
            </span>{" "}
            → {formatPriceCompact(named.packPriceMinor, pack.currency)} on{" "}
            {context.label}
          </p>
        ) : null}

        <p className="mt-1.5 text-[12px] text-text-secondary">{validity}</p>
      </div>
    </Link>
  );
}
